
import React, { useState, useEffect, useRef } from 'react';
import { Conversation, Message, ConversationResponse, ConversationDetails, MerchantSendMessageRequest } from '../types';
import { Send, User, Bot, PlusCircle, Trash2, MessageSquare, Loader2, Pencil, Download } from 'lucide-react';
import Button from '../components/ui/Button';
import { api } from '../services/api';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { handleApiError } from '../utils/errorHandling';
import { base64ToBlob, isNumericColumn, normalizeColumns, AnalyticsColumn } from '../utils/analyticsHelpers';

interface AnalyticsPayload {
    chart?: { base64: string; format: 'png' | 'svg' };
    exports?: { format: 'csv' | 'xlsx' | 'json'; mime_type: string; filename: string; base64?: string; content?: string }[];
    rows?: Record<string, unknown>[];
    columns?: AnalyticsColumn[];
    rowCount?: number;
    sql?: string;
}

interface ExtendedMessage extends Message, AnalyticsPayload {
    error?: string;
    isRetrying?: boolean;
    originalContent?: string;
}

const ConversationsPage: React.FC = () => {
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ExtendedMessage[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isLoadingConversations, setIsLoadingConversations] = useState(true);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const messagesEndRef = useRef<null | HTMLDivElement>(null);
    const skipNextLoadRef = useRef<boolean>(false);
    const messageIdCounterRef = useRef<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState('');
    const { subscriptionError, setSubscriptionError } = useSubscriptionError();
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
    const [editingMessageContent, setEditingMessageContent] = useState('');
    const [editingMessageOriginalContent, setEditingMessageOriginalContent] = useState('');
    const [isUpdatingMessage, setIsUpdatingMessage] = useState(false);

    useEffect(() => {
        loadConversations();
    }, []);

    useEffect(() => {
        setEditingMessageId(null);
        setEditingMessageContent('');
        setEditingMessageOriginalContent('');
        setIsTyping(false);
        setError(null);

        if (skipNextLoadRef.current) {
            skipNextLoadRef.current = false;
            return;
        }
        if (selectedConversationId) {
            loadConversationMessages(selectedConversationId);
        } else {
            setMessages([]);
        }
    }, [selectedConversationId]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    const loadConversations = async () => {
        try {
            setIsLoadingConversations(true);
            const response = await api.getConversationHistory(1, 20);
            setConversations(response.data || []);
        } catch (err: any) {
            handleApiError(err, setError, setSubscriptionError);
            setConversations([]);
        } finally {
            setIsLoadingConversations(false);
        }
    };

    const loadConversationMessages = async (conversationId: string) => {
        try {
            setIsLoadingMessages(true);
            setError(null);

            if (conversationId.startsWith('new-')) {
                setMessages([]);
                return;
            }

            const response = await api.getConversationMessages(conversationId, 1, 50);

            const mappedMessages = (response.data || []).map((msg: any, index: number) => {
                let sender = msg.sender || msg.type || msg.role || 'assistant';

                const messageIndex = response.data.length - 1 - index;
                const inferredSender = messageIndex % 2 === 0 ? 'user' : 'assistant';
                
                const analyticsData = msg.analyticsData || msg.metadata?.analyticsData || {};
                
                const mappedMsg: ExtendedMessage = {
                    id: msg.id || msg._id || msg.messageId,
                    sender: inferredSender,
                    content: msg.content || msg.text || '',
                    timestamp: msg.timestamp || msg.createdAt || new Date().toISOString(),
                    chart: analyticsData.chart,
                    exports: analyticsData.exports,
                    rows: analyticsData.rows,
                    columns: analyticsData.columns,
                    rowCount: analyticsData.rowCount,
                    sql: analyticsData.sql
                };
                return mappedMsg;
            });
            
            const sortedMessages = mappedMessages.reverse();
            setMessages(sortedMessages);

        } catch (err: any) {
            console.error('Error loading messages:', err);
            handleApiError(err, setError, setSubscriptionError);
            setMessages([]);
        } finally {
            setIsLoadingMessages(false);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const generateMessageId = (prefix: string): string => {
        messageIdCounterRef.current += 1;
        return `${prefix}-${Date.now()}-${messageIdCounterRef.current}`;
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        setError(null);
        const messageContent = newMessage.trim();
        setNewMessage('');
        setIsTyping(true);

        const userMessage: Message = {
            id: generateMessageId('user'),
            sender: 'user',
            content: messageContent,
            timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMessage]);

        try {
            const messageData: MerchantSendMessageRequest = {
                message: messageContent,
                conversationId: selectedConversationId && !selectedConversationId.startsWith('new-')
                    ? selectedConversationId
                    : undefined,
                options: {
                    channel: 'web'
                }
            };

            const response = await api.sendMessageStream(messageData);

            if (!response.ok) {
                const errorText = await response.text().catch(() => response.statusText);
                const error: any = new Error(`HTTP ${response.status}: ${errorText}`);
                error.status = response.status;
                error.response = { status: response.status };
                throw error;
            }

            if (!response.body) {
                throw new Error("Response body is null");
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let contentBuffer = '';
            let buffer = '';
            let finalConversationId = selectedConversationId;
            let assistantMessageId: string | null = null;
            let analyticsData: any = null;
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.substring(6).trim();
                            if (!jsonStr || jsonStr === '[DONE]') continue;

                            const json = JSON.parse(jsonStr);

                            const isTasks = json.type === 'tasks' || (!!json.tasks && Array.isArray(json.tasks));
                            if (isTasks) {
                                const tasksArr = json.tasks ?? [];
                                if (tasksArr.length > 0) {
                                    const analyticsTask = tasksArr.find((t: any) => t.id === 'analytics.result');
                                    if (analyticsTask && analyticsTask.params) {
                                        analyticsData = analyticsTask.params;

                                        if (assistantMessageId) {
                                            setMessages(prev => prev.map(msg =>
                                                msg.id === assistantMessageId
                                                ? {
                                                    ...msg,
                                                    chart: analyticsTask.params.chart,
                                                    exports: analyticsTask.params.exports,
                                                    rows: analyticsTask.params.rows,
                                                    columns: analyticsTask.params.columns,
                                                    rowCount: analyticsTask.params.rowCount,
                                                    sql: analyticsTask.params.sql,
                                                } as ExtendedMessage
                                                : msg
                                            ));
                                        }
                                    }
                                }
                            } else if ((json.type === 'chunk' && json.content) || (json.content && !json.type)) {
                                contentBuffer += json.content;
                                
                                if (!assistantMessageId) {
                                    assistantMessageId = generateMessageId('assistant');
                                    const assistantMessage: ExtendedMessage = {
                                        id: assistantMessageId,
                                        sender: 'assistant',
                                        content: contentBuffer,
                                        timestamp: new Date().toISOString(),
                                    };
                                    setMessages(prev => [...prev, assistantMessage]);
                                } else {
                                    setMessages(prev => prev.map(msg =>
                                        msg.id === assistantMessageId
                                        ? { ...msg, content: contentBuffer }
                                        : msg
                                    ));
                                }
                            } else if (json.type === 'error') {
                                const errorMessage = json.error || 'An error occurred while processing your message';

                                if (!assistantMessageId) {
                                    assistantMessageId = generateMessageId('assistant-error');
                                }
                                
                                const errorAssistantMessage: ExtendedMessage = {
                                    id: assistantMessageId,
                                    sender: 'assistant',
                                    content: `❌ ${errorMessage}`,
                                    timestamp: new Date().toISOString(),
                                    error: errorMessage,
                                    originalContent: messageContent,
                                };
                                setMessages(prev => [...prev, errorAssistantMessage]);

                                setError(errorMessage);

                            } else if (json.type === 'end') {
                                if (analyticsData && assistantMessageId) {
                                    setMessages(prev => prev.map(msg =>
                                        msg.id === assistantMessageId
                                        ? {
                                            ...msg,
                                            chart: analyticsData.chart || msg.chart,
                                            exports: analyticsData.exports || msg.exports,
                                            rows: analyticsData.rows || msg.rows,
                                            columns: analyticsData.columns || msg.columns,
                                            rowCount: analyticsData.rowCount || msg.rowCount,
                                            sql: analyticsData.sql || msg.sql,
                                        } as ExtendedMessage
                                        : msg
                                    ));
                                }

                                const serverId = json._id || json.conversationId;
                                if (serverId && finalConversationId !== serverId) {
                                    finalConversationId = serverId;

                                    if (selectedConversationId?.startsWith('new-')) {
                                        const oldTempId = selectedConversationId;
                                        const newConversation: Conversation = {
                                            _id: serverId,
                                            title: `Conversation ${new Date().toLocaleDateString()}`,
                                            lastMessagePreview: messageContent.length > 50
                                                ? messageContent.substring(0, 50) + '...'
                                                : messageContent,
                                            lastMessageAt: new Date().toISOString(),
                                            messageCount: 2
                                        };

                                        setConversations(prev => {
                                            const filtered = (prev || []).filter(c => c._id !== oldTempId);
                                            return [newConversation, ...filtered];
                                        });

                                        skipNextLoadRef.current = true;
                                        setSelectedConversationId(serverId);
                                    }
                                }
                            }
                        } catch (parseError) {
                        }
                    }
                }
            }

            if (!assistantMessageId && !error) {
                const fallbackMessage: Message = {
                    id: generateMessageId('assistant-fallback'),
                    sender: 'assistant',
                    content: '❌ No response received from the server.',
                    timestamp: new Date().toISOString(),
                };
                setMessages(prev => [...prev, fallbackMessage]);
                setError('No response received from the server.');
            }

        } catch (err: any) {
            const errorMessage = err.message || 'Failed to get response. Please try again.';

            const statusCode = err.response?.status || err.status;
            if (statusCode === 402 || statusCode === 429) {
                setIsRateLimited(true);
                setRateLimitMessage(
                    statusCode === 402 
                        ? 'Subscription required. Please upgrade your plan to continue.'
                        : 'Rate limit exceeded. Please wait a moment before sending more messages.'
                );
            }
            
            handleApiError(err, setError, setSubscriptionError);

            const errorAssistantMessage: ExtendedMessage = {
                id: generateMessageId('assistant-error'),
                sender: 'assistant',
                content: `❌ ${errorMessage}`,
                timestamp: new Date().toISOString(),
                error: errorMessage,
                originalContent: messageContent,
            };
            setMessages(prev => [...prev, errorAssistantMessage]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleEditMessage = (messageId: string) => {
        const messageToEdit = messages.find(msg => msg.id === messageId);
        if (!messageToEdit || !messageToEdit.originalContent) return;

        setNewMessage(messageToEdit.originalContent);

        setMessages(prev => prev.filter(msg => msg.id !== messageId));

        setError(null);
    };

    const handleRetryMessage = async (messageId: string) => {
        const messageToRetry = messages.find(msg => msg.id === messageId);
        if (!messageToRetry || !messageToRetry.originalContent) return;

        const messageContent = messageToRetry.originalContent;

        setMessages(prev => prev.filter(msg => msg.id !== messageId));

        setError(null);
        setIsTyping(true);

        const userMessage: Message = {
            id: generateMessageId('user'),
            sender: 'user',
            content: messageContent,
            timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMessage]);

        try {
            const messageData: MerchantSendMessageRequest = {
                message: messageContent,
                conversationId: selectedConversationId && !selectedConversationId.startsWith('new-')
                    ? selectedConversationId
                    : undefined,
                options: {
                    channel: 'web'
                }
            };

            const response = await api.sendMessageStream(messageData);

            if (!response.ok) {
                const errorText = await response.text().catch(() => response.statusText);
                const error: any = new Error(`HTTP ${response.status}: ${errorText}`);
                error.status = response.status;
                error.response = { status: response.status };
                throw error;
            }

            if (!response.body) {
                throw new Error("Response body is null");
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let contentBuffer = '';
            let buffer = '';
            let finalConversationId = selectedConversationId;
            let assistantMessageId: string | null = null;
            let analyticsData: any = null;
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.substring(6).trim();
                            if (!jsonStr || jsonStr === '[DONE]') continue;

                            const json = JSON.parse(jsonStr);

                            const isTasks = json.type === 'tasks' || (!!json.tasks && Array.isArray(json.tasks));
                            if (isTasks) {
                                const tasksArr = json.tasks ?? [];
                                if (tasksArr.length > 0) {
                                    const analyticsTask = tasksArr.find((t: any) => t.id === 'analytics.result');
                                    if (analyticsTask && analyticsTask.params) {
                                        analyticsData = analyticsTask.params;
                                        if (assistantMessageId) {
                                            setMessages(prev => prev.map(msg =>
                                                msg.id === assistantMessageId
                                                ? {
                                                    ...msg,
                                                    chart: analyticsTask.params.chart,
                                                    exports: analyticsTask.params.exports,
                                                    rows: analyticsTask.params.rows,
                                                    columns: analyticsTask.params.columns,
                                                    rowCount: analyticsTask.params.rowCount,
                                                    sql: analyticsTask.params.sql,
                                                } as ExtendedMessage
                                                : msg
                                            ));
                                        }
                                    }
                                }
                            } else if ((json.type === 'chunk' && json.content) || (json.content && !json.type)) {
                                contentBuffer += json.content;
                                
                                if (!assistantMessageId) {
                                    assistantMessageId = generateMessageId('assistant');
                                    const assistantMessage: ExtendedMessage = {
                                        id: assistantMessageId,
                                        sender: 'assistant',
                                        content: contentBuffer,
                                        timestamp: new Date().toISOString(),
                                    };
                                    setMessages(prev => [...prev, assistantMessage]);
                                } else {
                                    setMessages(prev => prev.map(msg =>
                                        msg.id === assistantMessageId
                                        ? { ...msg, content: contentBuffer }
                                        : msg
                                    ));
                                }
                            } else if (json.type === 'error') {
                                const errorMessage = json.error || 'An error occurred while processing your message';
                                
                                if (!assistantMessageId) {
                                    assistantMessageId = generateMessageId('assistant-error');
                                }
                                
                                const errorAssistantMessage: ExtendedMessage = {
                                    id: assistantMessageId,
                                    sender: 'assistant',
                                    content: `❌ ${errorMessage}`,
                                    timestamp: new Date().toISOString(),
                                    error: errorMessage,
                                    originalContent: messageContent,
                                };
                                setMessages(prev => [...prev, errorAssistantMessage]);
                                setError(errorMessage);
                            } else if (json.type === 'end') {
                                if (analyticsData && assistantMessageId) {
                                    setMessages(prev => prev.map(msg =>
                                        msg.id === assistantMessageId
                                        ? {
                                            ...msg,
                                            chart: analyticsData.chart || msg.chart,
                                            exports: analyticsData.exports || msg.exports,
                                            rows: analyticsData.rows || msg.rows,
                                            columns: analyticsData.columns || msg.columns,
                                            rowCount: analyticsData.rowCount || msg.rowCount,
                                            sql: analyticsData.sql || msg.sql,
                                        } as ExtendedMessage
                                        : msg
                                    ));
                                }
                                
                                const serverId = json._id || json.conversationId;
                                if (serverId && finalConversationId !== serverId) {
                                    finalConversationId = serverId;
                                    if (selectedConversationId?.startsWith('new-')) {
                                        const oldTempId = selectedConversationId;
                                        const newConversation: Conversation = {
                                            _id: serverId,
                                            title: `Conversation ${new Date().toLocaleDateString()}`,
                                            lastMessagePreview: messageContent.length > 50
                                                ? messageContent.substring(0, 50) + '...'
                                                : messageContent,
                                            lastMessageAt: new Date().toISOString(),
                                            messageCount: 2
                                        };
                                        setConversations(prev => {
                                            const filtered = (prev || []).filter(c => c._id !== oldTempId);
                                            return [newConversation, ...filtered];
                                        });
                                        skipNextLoadRef.current = true;
                                        setSelectedConversationId(serverId);
                                    }
                                }
                            }
                        } catch (parseError) {
                        }
                    }
                }
            }

            if (!assistantMessageId && !error) {
                const fallbackMessage: Message = {
                    id: generateMessageId('assistant-fallback'),
                    sender: 'assistant',
                    content: '❌ No response received from the server.',
                    timestamp: new Date().toISOString(),
                };
                setMessages(prev => [...prev, fallbackMessage]);
                setError('No response received from the server.');
            }

        } catch (err: any) {
            const errorMessage = err.message || 'Failed to get response. Please try again.';
            
            const statusCode = err.response?.status || err.status;
            if (statusCode === 402 || statusCode === 429) {
                setIsRateLimited(true);
                setRateLimitMessage(
                    statusCode === 402 
                        ? 'Subscription required. Please upgrade your plan to continue.'
                        : 'Rate limit exceeded. Please wait a moment before sending more messages.'
                );
            }
            
            handleApiError(err, setError, setSubscriptionError);

            const errorAssistantMessage: ExtendedMessage = {
                id: generateMessageId('assistant-error'),
                sender: 'assistant',
                content: `❌ ${errorMessage}`,
                timestamp: new Date().toISOString(),
                error: errorMessage,
                originalContent: messageContent,
            };
            setMessages(prev => [...prev, errorAssistantMessage]);
        } finally {
            setIsTyping(false);
        }
    };

    const handleStartEditMessage = (message: ExtendedMessage) => {
        setEditingMessageId(message.id);
        setEditingMessageContent(message.content);
        setEditingMessageOriginalContent(message.content);
        setError(null);
    };

    const handleCancelEditMessage = () => {
        setEditingMessageId(null);
        setEditingMessageContent('');
        setEditingMessageOriginalContent('');
    };

    const handleUpdateMessage = async () => {
        if (!editingMessageId || !selectedConversationId) {
            return;
        }

        const trimmedContent = editingMessageContent.trim();
        if (!trimmedContent) {
            return;
        }

        if (trimmedContent === editingMessageOriginalContent) {
            handleCancelEditMessage();
            return;
        }

        const previousMessages = messages.map(msg => ({ ...msg }));
        const previousConversations = (conversations || []).map(conv => ({ ...conv }));
        const isLatestMessage = previousMessages[previousMessages.length - 1]?.id === editingMessageId;

        setIsUpdatingMessage(true);

        setMessages(prev => prev.map(msg =>
            msg.id === editingMessageId ? { ...msg, content: trimmedContent } : msg
        ));

        const editedMessageIndex = messages.findIndex(msg => msg.id === editingMessageId);
        if (editedMessageIndex !== -1) {
            setMessages(prev => prev.slice(0, editedMessageIndex + 1));
        }

        setConversations(prev => (prev || []).map(conv => {
            if (conv._id !== selectedConversationId) {
                return conv;
            }

            if (!isLatestMessage && conv.lastMessagePreview !== editingMessageOriginalContent) {
                return conv;
            }

            return {
                ...conv,
                lastMessagePreview: trimmedContent,
            };
        }));

        try {
            const isPersistentConversation = !selectedConversationId.startsWith('new-');
            const isTemporaryMessage = editingMessageId.startsWith('user-') || editingMessageId.startsWith('assistant-');
            
            if (!isPersistentConversation || isTemporaryMessage) {
                setEditingMessageId(null);
                setEditingMessageContent('');
                setEditingMessageOriginalContent('');
                setIsUpdatingMessage(false);
                return;
            }

            setEditingMessageId(null);
            setEditingMessageContent('');
            setEditingMessageOriginalContent('');
            setIsUpdatingMessage(false);

            setIsTyping(true);

            const response = await api.updateConversationMessageStream(selectedConversationId, editingMessageId, { message: trimmedContent });

            if (!response.ok) {
                const errorText = await response.text().catch(() => response.statusText);
                const error: any = new Error(`HTTP ${response.status}: ${errorText}`);
                error.status = response.status;
                error.response = { status: response.status };
                throw error;
            }

            if (!response.body) {
                throw new Error("Response body is null");
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let contentBuffer = '';
            let buffer = '';
            let assistantMessageId: string | null = null;
            let analyticsData: any = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.substring(6).trim();
                            if (!jsonStr || jsonStr === '[DONE]') continue;

                            const json = JSON.parse(jsonStr);

                            const isTasks = json.type === 'tasks' || (!!json.tasks && Array.isArray(json.tasks));
                            if (isTasks) {
                                const tasksArr = json.tasks ?? [];
                                if (tasksArr.length > 0) {
                                    const analyticsTask = tasksArr.find((t: any) => t.id === 'analytics.result');
                                    if (analyticsTask && analyticsTask.params) {
                                        analyticsData = analyticsTask.params;
                                        
                                        if (assistantMessageId) {
                                            setMessages(prev => prev.map(msg =>
                                                msg.id === assistantMessageId
                                                ? {
                                                    ...msg,
                                                    chart: analyticsTask.params.chart,
                                                    exports: analyticsTask.params.exports,
                                                    rows: analyticsTask.params.rows,
                                                    columns: analyticsTask.params.columns,
                                                    rowCount: analyticsTask.params.rowCount,
                                                    sql: analyticsTask.params.sql,
                                                } as ExtendedMessage
                                                : msg
                                            ));
                                        }
                                    }
                                }
                            } else if ((json.type === 'chunk' && json.content) || (json.content && !json.type)) {
                                contentBuffer += json.content;
                                
                                if (!assistantMessageId) {
                                    assistantMessageId = generateMessageId('assistant');
                                    const assistantMessage: ExtendedMessage = {
                                        id: assistantMessageId,
                                        sender: 'assistant',
                                        content: contentBuffer,
                                        timestamp: new Date().toISOString(),
                                    };
                                    setMessages(prev => [...prev, assistantMessage]);
                                } else {
                                    setMessages(prev => prev.map(msg =>
                                        msg.id === assistantMessageId
                                        ? { ...msg, content: contentBuffer }
                                        : msg
                                    ));
                                }
                            } else if (json.type === 'error') {
                                const errorMessage = json.error || 'An error occurred while updating your message';
                                
                                if (!assistantMessageId) {
                                    assistantMessageId = generateMessageId('assistant-error');
                                }
                                
                                const errorAssistantMessage: ExtendedMessage = {
                                    id: assistantMessageId,
                                    sender: 'assistant',
                                    content: `❌ ${errorMessage}`,
                                    timestamp: new Date().toISOString(),
                                    error: errorMessage,
                                };
                                setMessages(prev => [...prev, errorAssistantMessage]);
                                setError(errorMessage);
                            } else if (json.type === 'end') {
                                if (analyticsData && assistantMessageId) {
                                    setMessages(prev => prev.map(msg =>
                                        msg.id === assistantMessageId
                                        ? {
                                            ...msg,
                                            chart: analyticsData.chart || msg.chart,
                                            exports: analyticsData.exports || msg.exports,
                                            rows: analyticsData.rows || msg.rows,
                                            columns: analyticsData.columns || msg.columns,
                                            rowCount: analyticsData.rowCount || msg.rowCount,
                                            sql: analyticsData.sql || msg.sql,
                                        } as ExtendedMessage
                                        : msg
                                    ));
                                }
                            }
                        } catch (parseError) {
                        }
                    }
                }
            }

            if (!assistantMessageId && !error) {
                const fallbackMessage: Message = {
                    id: generateMessageId('assistant-fallback'),
                    sender: 'assistant',
                    content: '❌ No response received from the server.',
                    timestamp: new Date().toISOString(),
                };
                setMessages(prev => [...prev, fallbackMessage]);
                setError('No response received from the server.');
            }
        } catch (err: any) {
            const errorMessage = err.message || 'Failed to update message. Please try again.';
            handleApiError(err, setError, setSubscriptionError);
            setMessages(previousMessages);
            setConversations(previousConversations);
        } finally {
            setIsTyping(false);
        }
    };

    const handleNewConversation = () => {
        const newConv: Conversation = {
            _id: generateMessageId('new'),
            title: 'New Conversation',
            lastMessagePreview: 'Start a conversation...',
            lastMessageAt: new Date().toISOString(),
            messageCount: 0,
        };
        setConversations(prev => [newConv, ...(prev || [])]);
        setSelectedConversationId(newConv._id);
        setMessages([]);
    };

    const handleDeleteConversation = async (conversationId: string) => {
        if (!conversationId) {
            return;
        }

        const previousConversations = [...(conversations || [])];
        const previousMessages = messages.map(msg => ({ ...msg }));
        const wasSelected = selectedConversationId === conversationId;

        setConversations(prev => (prev || []).filter(c => c._id !== conversationId));

        if (wasSelected) {
            setSelectedConversationId(null);
            setMessages([]);
            setEditingMessageId(null);
            setEditingMessageContent('');
            setEditingMessageOriginalContent('');
        }

        if (conversationId.startsWith('new-')) {
            return;
        }

        try {
            await api.deleteConversation(conversationId);
        } catch (err: any) {
            handleApiError(err, setError, setSubscriptionError);
            setConversations(previousConversations);
            if (wasSelected) {
                setSelectedConversationId(conversationId);
                setMessages(previousMessages);
            }
        }
    };

    const handleStartRename = (conversationId: string, currentTitle: string) => {
        setEditingConversationId(conversationId);
        setEditingTitle(currentTitle);
    };

    const handleCancelRename = () => {
        setEditingConversationId(null);
        setEditingTitle('');
    };

    const handleSaveRename = async (conversationId: string) => {
        const trimmedTitle = editingTitle.trim();
        const targetConversation = (conversations || []).find(conv => conv._id === conversationId);

        if (!targetConversation) {
            setEditingConversationId(null);
            setEditingTitle('');
            return;
        }

        const previousTitle = targetConversation.title;

        if (!trimmedTitle || trimmedTitle === previousTitle) {
            setEditingConversationId(null);
            setEditingTitle('');
            return;
        }

        setConversations(prev => (prev || []).map(conv => 
            conv._id === conversationId 
                ? { ...conv, title: trimmedTitle }
                : conv
        ));

        setEditingConversationId(null);
        setEditingTitle('');

        if (conversationId.startsWith('new-')) {
            return;
        }

        try {
            await api.updateConversation(conversationId, { title: trimmedTitle });
        } catch (err: any) {
            handleApiError(err, setError, setSubscriptionError);
            setConversations(prev => (prev || []).map(conv => 
                conv._id === conversationId 
                    ? { ...conv, title: previousTitle }
                    : conv
            ));
        }
    };

    const handleRenameKeyPress = (e: React.KeyboardEvent, conversationId: string) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            void handleSaveRename(conversationId);
        } else if (e.key === 'Escape') {
            handleCancelRename();
        }
    };

    const formatMessageContent = (content: string) => {
        return content.split('\n').map((line, index) => (
            <span key={index}>
                {line}
                {index < content.split('\n').length - 1 && <br />}
            </span>
        ));
    };

    const AnalyticsData: React.FC<{ msg: ExtendedMessage }> = ({ msg }) => {
        const [showAllRows, setShowAllRows] = React.useState(false);
        
        const shouldRenderChart = msg.chart && msg.rows && msg.rows.length > 0;
        
        let displayColumns: Array<{ key: string; label: string }> = [];
        if (shouldRenderChart && msg.rows && msg.rows.length > 0) {
            const firstRow = msg.rows[0];
            displayColumns = Object.keys(firstRow).map(key => ({
                key,
                label: key
            }));
        } else {
            const normalizedCols = normalizeColumns(msg.columns);
            displayColumns = normalizedCols.length > 0 ? normalizedCols : [];
        }

        const maxRowsToShow = showAllRows ? (msg.rows?.length ?? 0) : 10;

        return (
            <>
                {/* Removed SQL Query section per request */}
                
                {/* Render chart - backend ensures data is valid for visualization */}
                {shouldRenderChart && (
                    <div className="mt-3 bg-white dark:bg-gray-800 rounded-lg p-3">
                        <img 
                            src={`data:image/${msg.chart.format};base64,${msg.chart.base64}`}
                            alt="Analytics Chart"
                            className="w-full h-auto rounded"
                        />
                        {msg.rowCount && (
                            <div className="text-xs text-gray-700 dark:text-gray-400 mt-2">
                                {msg.rowCount} rows analyzed
                            </div>
                        )}
                    </div>
                )}
                
                {/* Always show exports if available, regardless of chart */}
                {msg.exports && msg.exports.length > 0 && (
                    <div className="mt-3 flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg p-3">
                        <div className="text-sm text-gray-800 dark:text-gray-300">
                            {msg.rowCount ? `${msg.rowCount} rows` : 'Data'} available for download
                        </div>
                        <div className="flex gap-2">
                            {msg.exports.map((exp, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        const blob = exp.base64 
                                            ? base64ToBlob(exp.base64, exp.mime_type)
                                            : new Blob([exp.content || ''], { type: exp.mime_type });
                                        
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = exp.filename;
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }}
                                    className="flex items-center gap-1 px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary-hover transition-colors"
                                >
                                    <Download className="w-3 h-3" />
                                    Download {exp.format.toUpperCase()}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                
                {msg.rows && msg.rows.length > 0 && (
                    <div className="mt-3 bg-white dark:bg-gray-800 rounded-lg p-3 overflow-x-auto">
                        <table className="min-w-full text-xs">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-gray-700">
                                    {displayColumns.slice(0, 5).map((col, idx) => (
                                        <th key={idx} className="text-left py-2 px-3 font-semibold text-gray-800 dark:text-gray-300">
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {msg.rows.slice(0, maxRowsToShow).map((row, rowIdx) => (
                                    <tr key={rowIdx} className="border-b border-gray-100 dark:border-gray-700">
                                        {displayColumns.slice(0, 5).map((col, colIdx) => (
                                            <td key={colIdx} className="py-2 px-3 text-gray-800 dark:text-gray-200">
                                                {String(row[col.key] ?? '')}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {msg.rows.length > 10 && (
                            <div className="text-xs text-gray-700 dark:text-gray-400 mt-2 text-center">
                                <span>Showing {Math.min(maxRowsToShow, msg.rows.length)} of {msg.rows.length} rows</span>
                                {!showAllRows && msg.rows.length > 10 && (
                                    <button
                                        onClick={() => setShowAllRows(true)}
                                        className="ml-2 text-primary dark:text-primary hover:underline"
                                    >
                                        Show more
                                    </button>
                                )}
                                {showAllRows && (
                                    <button
                                        onClick={() => setShowAllRows(false)}
                                        className="ml-2 text-primary dark:text-primary hover:underline"
                                    >
                                        Show less
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </>
        );
    };

    const [showConversationsList, setShowConversationsList] = useState(true);

    return (
        <div className="flex h-full flex-col min-h-0 lg:flex-row relative">
            {/* Conversations List */}
            <div className={`w-full lg:w-1/3 h-full border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-border-color flex flex-col bg-white dark:bg-surface 
                ${selectedConversationId && !showConversationsList ? 'hidden lg:flex' : 'flex'} 
                h-full min-h-0`}>
                <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-border-color flex justify-between items-center flex-shrink-0">
                    <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-text-primary">Conversations</h2>
                    <Button variant="secondary" onClick={handleNewConversation} size="sm">
                        <PlusCircle className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">New</span>
                    </Button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto">
                    {isLoadingConversations ? (
                        <div className="flex items-center justify-center p-8">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-600 dark:text-text-secondary" />
                            <span className="ml-2 text-gray-600 dark:text-text-secondary">Loading conversations...</span>
                        </div>
                    ) : !conversations || conversations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-8 text-gray-600 dark:text-text-secondary">
                            <MessageSquare className="w-12 h-12 mb-4 opacity-50" />
                            <p className="text-center">No conversations yet</p>
                            <p className="text-sm text-center mt-2">Start a new conversation to begin chatting</p>
                        </div>
                    ) : (
                        conversations.map(conv => (
                            <div
                                key={conv._id}
                                onClick={() => {
                                    if (editingConversationId !== conv._id) {
                                        setSelectedConversationId(conv._id);
                                        setShowConversationsList(false);
                                    }
                                }}
                                className={`p-3 sm:p-4 cursor-pointer border-l-4 transition-colors group ${
                                    selectedConversationId === conv._id
                                        ? 'bg-blue-50 dark:bg-gray-700 border-primary hover:bg-blue-100 dark:hover:bg-gray-650'
                                        : 'border-transparent hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                            >
                                <div className="flex justify-between items-start">
                                    <div className="flex-1 min-w-0">
                                        {editingConversationId === conv._id ? (
                                            <input
                                                type="text"
                                                value={editingTitle}
                                                onChange={(e) => setEditingTitle(e.target.value)}
                                                onBlur={() => void handleSaveRename(conv._id)}
                                                onKeyDown={(e) => handleRenameKeyPress(e, conv._id)}
                                                className="w-full bg-white dark:bg-background border border-gray-300 dark:border-border-color rounded px-2 py-1 text-sm font-semibold text-gray-900 dark:text-text-primary focus:ring-2 focus:ring-primary focus:border-primary"
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <h3 className="font-semibold text-gray-900 dark:text-text-primary truncate">
                                                {conv.title}
                                            </h3>
                                        )}
                                        <p className="text-sm text-gray-700 dark:text-text-secondary truncate mt-1">
                                            {conv.lastMessagePreview}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-text-secondary mt-1">
                                            {new Date(conv.lastMessageAt).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 ml-2">
                                        {selectedConversationId === conv._id && editingConversationId !== conv._id && conv.messageCount >= 2 && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStartRename(conv._id, conv.title);
                                                }}
                                                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                                                title="Rename"
                                                aria-label="Rename Conversation"
                                            >
                                                <Pencil className="w-4 h-4 text-gray-700 dark:text-text-secondary hover:text-gray-900 dark:hover:text-text-primary" />
                                            </button>
                                        )}
                                        {selectedConversationId === conv._id && editingConversationId !== conv._id && (
                                            <Button
                                                variant="danger"
                                                size="icon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    void handleDeleteConversation(conv._id);
                                                }}
                                                className="lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                                                aria-label="Delete Conversation"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Chat Window */}
            <div className={`w-full lg:w-2/3 h-full flex min-h-0 flex-col bg-gray-50 dark:bg-background 
                ${selectedConversationId && !showConversationsList ? 'flex' : 'hidden lg:flex'}`}>
                {selectedConversationId ? (
                    <>
                        <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-border-color flex justify-between items-center flex-shrink-0">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <button
                                    onClick={() => setShowConversationsList(true)}
                                    className="lg:hidden p-2 -ml-2 rounded-lg dark:text-text-secondary text-light-text-secondary dark:hover:text-text-primary hover:text-light-text-primary dark:hover:bg-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
                                    aria-label="Back to conversations"
                                >
                                    <MessageSquare className="w-5 h-5" />
                                </button>
                                <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 dark:text-text-primary truncate">
                                    {conversations.find(c => c._id === selectedConversationId)?.title || 'Conversation'}
                                </h2>
                            </div>
                            <Button
                                variant="danger"
                                size="icon"
                                onClick={() => {
                                    if (selectedConversationId) {
                                        void handleDeleteConversation(selectedConversationId);
                                    }
                                }}
                                aria-label="Delete Conversation"
                                className="flex-shrink-0"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>

                        <div className="flex-1 min-h-0 p-3 sm:p-4 lg:p-6 overflow-y-auto space-y-3 sm:space-y-4 overscroll-contain">
                            {isLoadingMessages ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-6 h-6 animate-spin text-gray-600 dark:text-text-secondary" />
                                    <span className="ml-2 text-gray-600 dark:text-text-secondary">Loading messages...</span>
                                </div>
                            ) : messages.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 text-gray-600 dark:text-text-secondary">
                                    <MessageSquare className="w-16 h-16 mb-4 opacity-50" />
                                    <h3 className="text-lg font-semibold mb-2">Start a conversation</h3>
                                    <p className="text-center">Send a message to begin chatting with the AI assistant</p>
                                </div>
                            ) : (
                                messages.map(msg => {
                                    const isUserMessage = msg.sender === 'user';
                                    const isAssistantMessage = msg.sender === 'assistant';
                                    const isEditingThisMessage = editingMessageId === msg.id;
                                    const isTemporaryMessageId = msg.id.startsWith('user-') || msg.id.startsWith('assistant-');
                                    const canEditMessage = isUserMessage && !isTemporaryMessageId;
                                    const showEditButton = canEditMessage && !editingMessageId;

                                    return (
                                        <div key={msg.id} className={`flex items-start gap-2 sm:gap-3 ${isUserMessage ? 'justify-end' : ''}`}>
                                            {isAssistantMessage && (
                                                <div className="w-8 h-8 rounded-full bg-primary flex-shrink-0 flex items-center justify-center">
                                                    <Bot className="w-5 h-5 text-white" />
                                                </div>
                                            )}

                                            <div className={`max-w-[85%] sm:max-w-md lg:max-w-xl p-3 rounded-lg shadow-sm break-words overflow-wrap-anywhere ${
                                                isUserMessage
                                                    ? 'bg-primary text-white'
                                                    : msg.error
                                                        ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                                                        : 'bg-white dark:bg-surface border border-gray-200 dark:border-border-color'
                                            }`}>
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className={`text-sm leading-relaxed break-words ${
                                                        isUserMessage
                                                            ? 'text-white'
                                                            : msg.error
                                                                ? 'text-red-800 dark:text-red-200'
                                                                : 'text-gray-900 dark:text-text-primary'
                                                    } flex-1`}>
                                                        {formatMessageContent(msg.content)}
                                                    </div>
                                                    {showEditButton && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleStartEditMessage(msg)}
                                                            className="p-1 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
                                                            title="Edit message"
                                                            aria-label="Edit message"
                                                        >
                                                            <Pencil className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>

                                                {isEditingThisMessage && (
                                                    <div className="mt-3 bg-white dark:bg-gray-800 rounded-md p-3 shadow-sm">
                                                        <textarea
                                                            value={editingMessageContent}
                                                            onChange={(e) => setEditingMessageContent(e.target.value)}
                                                            rows={Math.max(3, editingMessageContent.split('\n').length)}
                                                            className="w-full resize-y rounded-md border border-gray-300 dark:border-gray-600 p-2 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                                        />
                                                        <div className="mt-2 flex justify-end gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={handleCancelEditMessage}
                                                                className="text-xs px-3 py-1 rounded bg-gray-500 dark:bg-gray-500 text-white hover:bg-gray-600 dark:hover:bg-gray-600 transition-colors"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={handleUpdateMessage}
                                                                disabled={isUpdatingMessage}
                                                                className="text-xs px-3 py-1 rounded bg-primary text-white hover:bg-primary-hover dark:hover:bg-primary-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                                            >
                                                                {isUpdatingMessage ? 'Saving...' : 'Save'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {msg.error && isAssistantMessage && (
                                                    <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-800">
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span className="text-xs text-red-600 dark:text-red-400">Message failed to send</span>
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => handleEditMessage(msg.id)}
                                                                    disabled={msg.isRetrying}
                                                                    className="text-xs px-3 py-1 bg-gray-500 dark:bg-gray-500 text-white rounded hover:bg-gray-600 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => handleRetryMessage(msg.id)}
                                                                    disabled={msg.isRetrying}
                                                                    className="text-xs px-3 py-1 bg-red-600 dark:bg-red-600 text-white rounded hover:bg-red-700 dark:hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    {msg.isRetrying ? 'Retrying...' : 'Retry'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {isAssistantMessage && !msg.error && <AnalyticsData msg={msg} />}
                                                
                                                <div className={`text-xs mt-2 ${
                                                    isUserMessage ? 'text-blue-100' : 'text-gray-600 dark:text-text-secondary'
                                                }`}>
                                                    {new Date(msg.timestamp).toLocaleTimeString([], {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </div>
                                            </div>

                                            {isUserMessage && (
                                                <div className="w-8 h-8 rounded-full bg-gray-400 dark:bg-gray-600 flex-shrink-0 flex items-center justify-center">
                                                    <User className="w-5 h-5 text-white" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}

                            {isTyping && (
                                <div className="flex items-start gap-2 sm:gap-3">
                                    <div className="w-8 h-8 rounded-full bg-primary flex-shrink-0 flex items-center justify-center">
                                        <Bot className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="max-w-[85%] sm:max-w-xl p-3 rounded-lg bg-white dark:bg-surface border border-gray-200 dark:border-border-color break-words">
                                        <div className="flex items-center gap-1">
                                            <span className="text-sm text-gray-700 dark:text-text-secondary">MentorosAI is typing</span>
                                            <div className="flex gap-1">
                                                <div className="w-1 h-1 bg-gray-700 dark:bg-text-secondary rounded-full animate-bounce"></div>
                                                <div className="w-1 h-1 bg-gray-700 dark:bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                                <div className="w-1 h-1 bg-gray-700 dark:bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-border-color flex-shrink-0 sticky bottom-0 bg-gray-50 dark:bg-background pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:pt-4">
                            {/* Rate limit / Subscription banner */}
                            {(isRateLimited || subscriptionError) && (
                                <div className="mb-3 p-2 sm:p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                                                {subscriptionError ? 'Subscription Issue' : 'Rate Limited'}
                                            </p>
                                            <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                                {rateLimitMessage || subscriptionError?.message || 'Unable to send messages at this time.'}
                                            </p>
                                        </div>
                                        {subscriptionError && (
                                            <button
                                                onClick={() => window.location.href = '/subscriptions'}
                                                className="text-xs px-2 sm:px-3 py-1 bg-yellow-600 dark:bg-yellow-600 text-white rounded hover:bg-yellow-700 dark:hover:bg-yellow-700 transition-colors whitespace-nowrap"
                                            >
                                                Upgrade Plan
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    placeholder={isRateLimited || subscriptionError ? "Messaging disabled" : "Type your message..."}
                                    className="flex-1 min-w-0 bg-white dark:bg-surface border border-gray-300 dark:border-border-color rounded-full py-2 sm:py-3 px-3 sm:px-4 text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={isTyping || isRateLimited || !!subscriptionError}
                                />
                                <Button
                                    type="submit"
                                    className="rounded-full p-2 sm:p-3 flex-shrink-0"
                                    size="icon"
                                    disabled={isTyping || !newMessage.trim() || isRateLimited || !!subscriptionError}
                                    title={isRateLimited || subscriptionError ? "Messaging disabled" : "Send message"}
                                >
                                    {isTyping ? (
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                        <Send className="w-5 h-5" />
                                    )}
                                </Button>
                            </form>
                            {error && !isRateLimited && !subscriptionError && (
                                <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                                    <span>⚠️</span>
                                    {error}
                                </p>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col justify-center items-center text-gray-600 dark:text-text-secondary">
                        <MessageSquare className="w-16 h-16 mb-4 opacity-50" />
                        <h2 className="text-xl font-semibold mb-2">Select a conversation</h2>
                        <p className="text-center">Choose a conversation from the sidebar or start a new one</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConversationsPage;
